import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Grid, DialogContent } from "@mui/material"

import i18n from "config/i18n"
import { useAppDispatch } from "redux/hooks"
import {
  withClient,
  withModalStep,
  withModalPaymentAmount,
  withPaymentMethods,
} from "hocs/features"
import {
  closeDialog,
  updateDialogStep,
} from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import { showAlert } from "redux/features/notice/noticeSlice"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import NewBatchPayment from "components/BatchPayment/Pay/New"
import NewBatchTransfer from "components/BatchPayment/Pay/Transfer"
import { PAYMENT_STEP_INITIAL, PAYMENT_STEP_TRANSFER } from "config"
import { DebtModalType, PaymentMethodList, ClientType } from "types"

type Props = DebtModalType &
  ClientType & {
    amount: number
    paymentMethods: PaymentMethodList
  } & { step: number }

const BatchPaymentPayModal = ({
  amount,
  paymentMethods,
  client,
  step = PAYMENT_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isBackHidden = step !== PAYMENT_STEP_TRANSFER

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: i18n.t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate("/")
  }

  const handleBatchPaymentCreateFailure = (error: string) => {
    dispatch(
      showAlert({
        message: error,
      }),
    )
  }

  const handleBack = () => {
    switch (step) {
      case PAYMENT_STEP_TRANSFER:
        dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
        return
      default:
        return
    }
  }

  const STEPS = [
    {
      label: PAYMENT_STEP_INITIAL,
      title: t("features.batch_payment.form.title"),
      component: (
        <NewBatchPayment
          amount={amount}
          paymentMethods={paymentMethods}
          onToggle={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_TRANSFER }))
          }
          onFailure={(error) => handleBatchPaymentCreateFailure(error)}
        />
      ),
    },
    {
      label: PAYMENT_STEP_TRANSFER,
      title: t("features.batch_payment.form.title"),
      component: (
        <NewBatchTransfer
          amount={amount}
          onBanklink={() =>
            dispatch(updateDialogStep({ step: PAYMENT_STEP_INITIAL }))
          }
          client={client}
        />
      ),
    },
  ]

  return (
    <DialogContent>
      <Back hidden={isBackHidden} onClick={handleBack} />
      <Close onClick={handleClose} />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalPaymentAmount,
  withClient,
  withPaymentMethods,
)(BatchPaymentPayModal)
